import { useEffect, useState } from 'react';

import { useAppSelector } from '@/shared/lib/redux';

/**
 * Fetches the signed-in user's profile photo from Microsoft Graph when an
 * Azure access token is present. Returns an object URL for the photo blob,
 * or `null` while unavailable.
 *
 * Extracted from the vendored `components/ui/avatar` so the design-system
 * layer stays pristine and can be overwritten on registry syncs.
 */
export function useAzureProfilePhoto(): string | null {
  const azureAccessToken = useAppSelector((state) => state.auth?.azureAccessToken);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!azureAccessToken) {
      return;
    }

    let isMounted = true;
    const fetchPhoto = async () => {
      try {
        const response = await fetch('https://graph.microsoft.com/v1.0/me/photo/$value', {
          headers: {
            Authorization: `Bearer ${azureAccessToken}`
          }
        });

        if (response.ok) {
          const blob = await response.blob();
          const url = URL.createObjectURL(blob);
          if (isMounted) {
            setPhotoUrl(url);
          }
        }
      } catch (error) {
        console.error('Error fetching Azure profile photo:', error);
      }
    };

    fetchPhoto();

    return () => {
      isMounted = false;
    };
  }, [azureAccessToken]);

  // Revoke the previous object URL once a new one replaces it (or on unmount)
  useEffect(() => {
    return () => {
      if (photoUrl) {
        URL.revokeObjectURL(photoUrl);
      }
    };
  }, [photoUrl]);

  return photoUrl;
}
